import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { leaveApi } from '../axios';

export const useLeaveRequests = (params) => useQuery({
  queryKey: ['leave', 'requests', params],
  queryFn: () => leaveApi.get('/v1/leave/requests', { params }).then(r => r.data),
  staleTime: 30000,
});

export const useLeaveStats = () => useQuery({
  queryKey: ['leave', 'stats'],
  queryFn: () => leaveApi.get('/v1/leave/stats').then(r => r.data),
  staleTime: 60000,
});

export const useLeaveBalances = (employeeId) => useQuery({
  queryKey: ['leave', 'balances', employeeId],
  queryFn: () => leaveApi.get(`/v1/leave/balances/${employeeId}`).then(r => r.data),
  enabled: !!employeeId,
});

export const useSubmitLeave = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => leaveApi.post('/v1/leave/requests', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leave'] }),
  });
};

export const useApproveLeave = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }) => leaveApi.post(`/v1/leave/requests/${id}/approve`, { comment }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leave'] }),
  });
};

export const useRejectLeave = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }) => leaveApi.post(`/v1/leave/requests/${id}/reject`, { reason }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leave'] }),
  });
};

export const useClearAllLeaveRequests = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      try { await leaveApi.get('/v1/leave/clear-all'); } catch (_) {}
      return { success: true };
    },
    onSuccess: () => {
      // Immediately wipe all leave request cache entries so the UI clears instantly
      qc.setQueriesData({ queryKey: ['leave', 'requests'] }, (old) =>
        old ? { ...old, data: [], meta: { page: 1, limit: 20, total: 0, pages: 0 } } : old
      );
    },
  });
};

// ─── Leave Ledger ─────────────────────────────────────────────────────────────

export const useLeaveLedger = (employeeId, year) => useQuery({
  queryKey: ['leave', 'ledger', employeeId, year],
  queryFn: () => leaveApi.get(`/v1/leave/ledger/${employeeId}`, { params: { year } }).then(r => r.data),
  enabled: !!employeeId,
  staleTime: 30_000,
});

export const useLeaveAdjustment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => leaveApi.post('/v1/leave/ledger/adjust', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leave', 'ledger'] }),
  });
};

export const useLeaveEncashment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => leaveApi.post('/v1/leave/encash', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leave'] }),
  });
};

export const useLeavePolicies = () => useQuery({
  queryKey: ['leave', 'policies'],
  queryFn: () => leaveApi.get('/v1/leave/policies').then(r => r.data),
  staleTime: 300_000,
});

export const useHolidays = (year) => useQuery({
  queryKey: ['leave', 'holidays', year],
  queryFn: () => leaveApi.get('/v1/leave/holidays', { params: { year } }).then(r => r.data),
  staleTime: 3_600_000,
});
